// ═══════════════════════════════════════════════════════════════
// useWorkspace.js — verticalstack workspace state manager
// Path: src/hooks/useWorkspace.js
//
// S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
//
// PURPOSE
//   Owns the "current workspace" state across the app. A user (Chris)
//   can be a member of multiple workspaces (3 LLCs: Retriever Ridge,
//   A-1 Advance, Red Oak Partners). useWorkspace tracks which one is
//   active right now.
//
// DESIGN
//   - State source: profile.workspaces (set by useAuth via the
//     getUserWorkspaces CF). No additional Firestore reads here.
//   - Persistence: currentWorkspaceId saved to localStorage so the
//     selection survives page reload.
//   - Provider pattern: wraps app once, exposes context via useWorkspace().
//
// USAGE
//   <AuthProvider>
//     <WorkspaceProvider>
//       <App />
//     </WorkspaceProvider>
//   </AuthProvider>
//
//   Inside any component:
//     const { currentWorkspaceId, currentWorkspace, availableWorkspaces,
//             setCurrentWorkspaceId, switchWorkspace, loading }
//       = useWorkspace();
//
// MULTI-WORKSPACE FLOW (Chris's case — 3 workspaces)
//   1. User logs in → useAuth hydrates profile.workspaces (3 entries)
//   2. WorkspaceProvider reads localStorage for last-selected workspaceId
//   3. If a stored ID is found AND it's still in availableWorkspaces:
//        currentWorkspaceId = stored ID
//      Else:
//        currentWorkspaceId = null → triggers WorkspacePicker render
//   4. User picks a workspace → setCurrentWorkspaceId(id) → persisted
//   5. User switches workspace from Shell top bar → switchWorkspace(id)
//      → same effect, separate function name for caller clarity
//
// SINGLE-WORKSPACE FLOW
//   If profile.workspaces.length === 1, currentWorkspaceId auto-sets
//   to that single workspace on mount. WorkspacePicker is skipped.
// ═══════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useAuth } from './useAuth';

const WorkspaceContext = createContext(null);

const STORAGE_KEY = 'vs.currentWorkspaceId';

// ─────────────────────────────────────────────────────────────
// Persistence helpers — wrapped in try/catch because localStorage
// can be unavailable (private browsing, server-side render, etc.)
// ─────────────────────────────────────────────────────────────
function readStoredWorkspaceId() {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    console.warn('[useWorkspace] localStorage read failed:', err.message);
    return null;
  }
}

function writeStoredWorkspaceId(id) {
  try {
    if (typeof window === 'undefined') return;
    if (id) {
      window.localStorage.setItem(STORAGE_KEY, id);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    console.warn('[useWorkspace] localStorage write failed:', err.message);
  }
}

export function WorkspaceProvider({ children }) {
  const { profile, loading: authLoading } = useAuth();
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Available workspaces come from useAuth's profile hydration.
  // Defensive: handle case where profile is null or workspaces missing.
  const availableWorkspaces = useMemo(() => {
    if (!profile || !Array.isArray(profile.workspaces)) return [];
    return profile.workspaces;
  }, [profile]);

  // ─────────────────────────────────────────────────────────────
  // Initialize currentWorkspaceId once profile.workspaces is ready.
  //
  // Preference order:
  //   1. localStorage value, IF still valid (workspace still in list)
  //   2. Auto-select if only one workspace
  //   3. null → WorkspacePicker shown by App.js root
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    if (!profile) {
      // Logged out — clear state
      setCurrentWorkspaceIdState(null);
      setInitialized(false);
      return;
    }

    if (initialized) return;

    if (availableWorkspaces.length === 0) {
      // Logged in but no workspace memberships. Stay null;
      // Shell will render an empty-state for this case.
      setCurrentWorkspaceIdState(null);
      setInitialized(true);
      return;
    }

    if (availableWorkspaces.length === 1) {
      // Single-workspace user — skip picker, auto-select
      const onlyId = availableWorkspaces[0].workspaceId;
      setCurrentWorkspaceIdState(onlyId);
      writeStoredWorkspaceId(onlyId);
      setInitialized(true);
      return;
    }

    // Multi-workspace user — try restoring last selection from localStorage
    const stored = readStoredWorkspaceId();
    const isStoredValid =
      stored && availableWorkspaces.some((w) => w.workspaceId === stored);

    if (isStoredValid) {
      setCurrentWorkspaceIdState(stored);
    } else {
      // No valid stored ID. Leave null so WorkspacePicker renders.
      setCurrentWorkspaceIdState(null);
      writeStoredWorkspaceId(null); // Clear stale value
    }
    setInitialized(true);
  }, [authLoading, profile, availableWorkspaces, initialized]);

  // ─────────────────────────────────────────────────────────────
  // Public setters. Both call the same underlying state update;
  // separate names for caller intent (picker vs switcher).
  // ─────────────────────────────────────────────────────────────
  const setCurrentWorkspaceId = (id) => {
    if (!id) {
      // Allow explicit null/undefined to clear (e.g., "back to picker")
      setCurrentWorkspaceIdState(null);
      writeStoredWorkspaceId(null);
      return;
    }
    const isValid = availableWorkspaces.some((w) => w.workspaceId === id);
    if (!isValid) {
      console.warn(
        `[useWorkspace] setCurrentWorkspaceId rejected: '${id}' not in availableWorkspaces`
      );
      return;
    }
    setCurrentWorkspaceIdState(id);
    writeStoredWorkspaceId(id);
  };

  const switchWorkspace = (id) => setCurrentWorkspaceId(id);

  // ─────────────────────────────────────────────────────────────
  // Convenience: derive the full current workspace object
  // ─────────────────────────────────────────────────────────────
  const currentWorkspace = useMemo(() => {
    if (!currentWorkspaceId) return null;
    return availableWorkspaces.find(
      (w) => w.workspaceId === currentWorkspaceId
    ) || null;
  }, [currentWorkspaceId, availableWorkspaces]);

  // Loading is true while auth is still hydrating OR while we haven't
  // yet decided initial workspace selection.
  const loading = authLoading || (profile && !initialized);

  const value = {
    currentWorkspaceId,
    currentWorkspace,
    availableWorkspaces,
    setCurrentWorkspaceId,
    switchWorkspace,
    loading,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (ctx === null) {
    throw new Error(
      '[useWorkspace] called outside <WorkspaceProvider>. ' +
      'Wrap your app: <AuthProvider><WorkspaceProvider>...</WorkspaceProvider></AuthProvider>'
    );
  }
  return ctx;
}

export default useWorkspace;
